import type { FilterRuleModel, OptimizationIDModel } from '@/shared/generated'

import { filter, pipe, prop, propEq, reduce } from 'lodash/fp'
import { AlertTriangleIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	MetricName,
	useRepricingStatisticQuery,
} from '@/entities/optimization-statistic'

import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { DeltaValue } from '@/shared/components/ui/delta-value'
import {
	DescriptionDetail,
	DescriptionGroup,
	DescriptionTerm,
} from '@/shared/components/ui/description-list'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

export const FinancialInsightsWidget = ({
	queryParams,
	isLoading: isDataLoading,
}: {
	queryParams: {
		optimizations: OptimizationIDModel[]
		filters: FilterRuleModel[]
	} | null
	isLoading: boolean
}) => {
	const { t } = useTranslation()

	const {
		data: repricingStatistic,
		isLoading,
		isError,
		error,
	} = useRepricingStatisticQuery(queryParams!, {
		enabled: !!queryParams && !isDataLoading,
		refetchOnMount: true,
		staleTime: 0,
		select: pipe(
			prop('metrics'),
			filter(propEq('group_type', null)),
			reduce((acc: Record<string, number>, item) => {
				acc[item.name] = item.value

				return acc
			}, {})
		),
	})
	if (isLoading || isDataLoading) {
		return <Skeleton className='h-10' />
	}
	if (isError) {
		return (
			<Alert variant='destructive'>
				<AlertTriangleIcon />
				<AlertDescription>
					{error.message || t('fatal_error_title')}
				</AlertDescription>
			</Alert>
		)
	}

	return (
		repricingStatistic &&
		!isLoading &&
		!isDataLoading && (
			<div className='flex justify-between'>
				<DescriptionGroup>
					<DescriptionTerm>
						{t('opt.summary.new_disc', { defaultValue: 'New Disc.' })}
					</DescriptionTerm>
					<DescriptionDetail>
						<span className='text-lg font-semibold'>
							{formatNumber(
								repricingStatistic[MetricName.DiscountFinal] || 0,
								NumberFormats.Percent
							)}
						</span>
						<DeltaValue
							className='ml-1 text-sm'
							value={getDeltaValue({
								current: repricingStatistic[MetricName.DiscountInit] || 0,
								new: repricingStatistic[MetricName.DiscountFinal] || 0,
							})}
						>
							{formatNumber(
								getDeltaValue({
									current: repricingStatistic[MetricName.DiscountInit] || 0,
									new: repricingStatistic[MetricName.DiscountFinal] || 0,
								}) || 0,
								NumberFormats.PercentSigned
							)}
						</DeltaValue>
					</DescriptionDetail>
				</DescriptionGroup>
				<DescriptionGroup>
					<DescriptionTerm>
						{t('opt.summary.current_disc', { defaultValue: 'Current Disc.' })}
					</DescriptionTerm>
					<DescriptionDetail>
						<span className='text-lg font-semibold'>
							{formatNumber(
								repricingStatistic[MetricName.DiscountInit] || 0,
								NumberFormats.Percent
							)}
						</span>
					</DescriptionDetail>
				</DescriptionGroup>
				<DescriptionGroup>
					<DescriptionTerm>
						{t('opt.summary.new_margin', { defaultValue: 'New Margin' })}
					</DescriptionTerm>
					<DescriptionDetail>
						<span className='text-lg font-semibold'>
							{formatNumber(
								repricingStatistic[MetricName.MarginFinal] || 0,
								NumberFormats.Percent
							)}
						</span>
						<DeltaValue
							className='ml-1 text-sm'
							value={getDeltaValue({
								current: repricingStatistic[MetricName.MarginInit] || 0,
								new: repricingStatistic[MetricName.MarginFinal] || 0,
							})}
						>
							{formatNumber(
								getDeltaValue({
									current: repricingStatistic[MetricName.MarginInit] || 0,
									new: repricingStatistic[MetricName.MarginFinal] || 0,
								}) || 0,
								NumberFormats.PercentSigned
							)}
						</DeltaValue>
					</DescriptionDetail>
				</DescriptionGroup>
				<DescriptionGroup>
					<DescriptionTerm>
						{t('opt.summary.current_margin', {
							defaultValue: 'Current Margin',
						})}
					</DescriptionTerm>
					<DescriptionDetail>
						<span className='text-lg font-semibold'>
							{formatNumber(
								repricingStatistic[MetricName.MarginInit] || 0,
								NumberFormats.Percent
							)}
						</span>
					</DescriptionDetail>
				</DescriptionGroup>
			</div>
		)
	)
}

const getDeltaValue = (values: { current: number; new: number }) =>
	Number(values.new) - Number(values.current) || 0
